import { ensureDocumentFonts } from '../model/fonts';
import { invalidateTextLayouts } from '../model/textLayout';
import { pageOf } from '../model/operations';
import { placement } from '../model/framing';
import { clipLength, exportDuration, isActive, pageDuration, pageVideos, sourceTime } from '../model/timeline';
import { drawPage } from './renderer';
import { preparePage } from './resources';
import { ArtExportError } from './exportArt';

// The MP4 of a page with videos (contract 2.13.0, renderer `studio-art-web-v1`): every frame
// painted by the same painter as the stage, at 1× (1080 px wide), 30 fps, H.264, with the
// clips' sound mixed at their volume in AAC 48 kHz stereo. It runs in the browser on
// WebCodecs (mediabunny, loaded on the first video export): each clip's file is decoded at
// exactly the frames it shows, never played in real time, so the file matches the timeline.
// A page shorter than Instagram's 3 s holds its last frame. Where the browser cannot encode
// AAC, a WASM encoder is loaded; where it cannot encode H.264 the export stops
// (`video_unsupported`).
export const VIDEO_EXPORT = { fps: 30, bitrate: 8e6, keyFrameSeconds: 2, audioBitrate: 128e3, sampleRate: 48000, channels: 2 };

// The time of every frame of a page's file.
export const frameTimes = (duration, fps = VIDEO_EXPORT.fps) => Array.from({ length: Math.max(1, Math.round(duration * fps)) }, (_, index) => index / fps);

// The frames a clip shows (their index in `times`) and the second of its file each one is,
// in order. Past the page's end (the 3 s hold) the page stays at its last moment.
export function clipSchedule(layer, times, pageEnd) {
  const frames = [];
  const timestamps = [];
  times.forEach((time, index) => {
    const at = Math.min(time, pageEnd);
    if (!isActive(layer, at)) return;
    frames.push(index);
    timestamps.push(sourceTime(layer, at));
  });
  return { frames, timestamps };
}

// How large a clip's frames need to be decoded: its painted size, never above the file's.
function decodeSize(layer) {
  const { dw, dh } = placement(layer.width, layer.height, layer.source.width, layer.source.height, layer);
  const scale = Math.min(1, Math.max(dw / layer.source.width, dh / layer.source.height));
  const even = value => Math.max(2, Math.round(value / 2) * 2);
  return scale >= 0.98 ? null : { width: even(layer.source.width * scale), height: even(layer.source.height * scale) };
}

async function wholeFile(mb, url) {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error('fetch');
  return new mb.Input({ source: new mb.BlobSource(await response.blob()), formats: mb.ALL_FORMATS });
}
// A file's input and tracks. A local file (an upload of this session, the mock) is read
// whole; a remote one by ranges, or whole when its host does not let ranges through CORS.
async function openFile(mb, url) {
  const tracksOf = async input => {
    const video = await input.getPrimaryVideoTrack();
    if (!video || !(await video.canDecode())) throw new Error('decode');
    const audio = await input.getPrimaryAudioTrack().catch(() => null);
    return { input, video, audio: audio && (await audio.canDecode()) ? audio : null };
  };
  if (/^(blob|data):/i.test(url)) return tracksOf(await wholeFile(mb, url));
  try {
    return await tracksOf(new mb.Input({ source: new mb.UrlSource(url, { requestInit: { mode: 'cors' } }), formats: mb.ALL_FORMATS }));
  } catch {
    return tracksOf(await wholeFile(mb, url));
  }
}

// A clip's sound as one buffer, from its trim's start to its end.
async function clipSound(mb, track, layer, audio) {
  let target = null;
  for await (const { buffer, timestamp } of new mb.AudioBufferSink(track).buffers(layer.trim.start, layer.trim.end)) {
    if (!target) target = audio.createBuffer(VIDEO_EXPORT.channels, Math.max(1, Math.ceil(clipLength(layer) * buffer.sampleRate)), buffer.sampleRate);
    const offset = Math.round((timestamp - layer.trim.start) * buffer.sampleRate);
    const skip = Math.max(0, -offset);
    for (let channel = 0; channel < VIDEO_EXPORT.channels; channel++) {
      const data = buffer.getChannelData(Math.min(channel, buffer.numberOfChannels - 1));
      if (skip < data.length) target.copyToChannel(data.subarray(skip), channel, Math.max(0, offset));
    }
  }
  return target;
}

// Every audible clip at its place on the page, at its volume, mixed into one buffer.
async function mixSound(mb, clips, files, duration) {
  const audible = clips.filter(layer => !layer.muted && layer.volume > 0 && files.get(layer.source.url)?.audio);
  if (!audible.length || typeof OfflineAudioContext === 'undefined') return null;
  const audio = new OfflineAudioContext(VIDEO_EXPORT.channels, Math.ceil(duration * VIDEO_EXPORT.sampleRate), VIDEO_EXPORT.sampleRate);
  let placed = 0;
  for (const layer of audible) {
    const sound = await clipSound(mb, files.get(layer.source.url).audio, layer, audio).catch(() => null);
    if (!sound) continue;
    const node = audio.createBufferSource();
    node.buffer = sound;
    const gain = audio.createGain();
    gain.gain.value = layer.volume;
    node.connect(gain).connect(audio.destination);
    node.start(layer.at, 0, Math.min(clipLength(layer), duration - layer.at));
    placed += 1;
  }
  return placed ? audio.startRendering() : null;
}

// AAC where the browser encodes it, else the WASM encoder (loaded once).
async function aacReady(mb) {
  const options = { numberOfChannels: VIDEO_EXPORT.channels, sampleRate: VIDEO_EXPORT.sampleRate, bitrate: VIDEO_EXPORT.audioBitrate };
  if (await mb.canEncodeAudio('aac', options)) return true;
  try {
    const { registerAacEncoder } = await import('@mediabunny/aac-encoder');
    registerAacEncoder();
  } catch { return false; }
  return mb.canEncodeAudio('aac', options);
}

// → { blob, width, height, mime, extension, pageId, duration, audio } where `audio` is
// `mixed` (the clips' sound is in), `none` (every clip is muted or silent) or `dropped` (the
// browser could not encode it). `onProgress(0…1)`.
export async function renderPageVideo(doc, { pageId = null, onProgress = () => {} } = {}) {
  const page = pageOf(doc, pageId);
  const clips = pageVideos(page).filter(layer => layer.visible);
  if (!clips.length) throw new ArtExportError('render');
  if (typeof VideoEncoder === 'undefined' || typeof document === 'undefined') throw new ArtExportError('video_unsupported');
  const fonts = await ensureDocumentFonts(doc, page.id);
  if (!fonts.ok) throw new ArtExportError('fonts', { families: fonts.missing });
  invalidateTextLayouts();
  const { failed } = await preparePage(page, 1);
  if (failed.length) throw new ArtExportError('images', { layerIds: failed });

  const mb = await import('mediabunny');
  const { width, height } = doc;
  const { fps } = VIDEO_EXPORT;
  if (!(await mb.canEncodeVideo('avc', { width, height, bitrate: VIDEO_EXPORT.bitrate, frameRate: fps }))) throw new ArtExportError('video_unsupported');

  // Each file once, with its tracks.
  const files = new Map();
  const unreadable = [];
  for (const layer of clips) {
    const url = layer.source.url;
    if (!url) { unreadable.push(layer.id); continue; }
    if (files.has(url)) continue;
    try { files.set(url, await openFile(mb, url)); } catch { unreadable.push(layer.id); }
  }
  const dispose = () => files.forEach(file => file.input.dispose?.());
  if (unreadable.length) { dispose(); throw new ArtExportError('videos', { layerIds: unreadable }); }

  try {
    const duration = exportDuration(page);
    const pageEnd = pageDuration(page);
    const times = frameTimes(duration, fps);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new ArtExportError('render');

    const target = new mb.BufferTarget();
    const output = new mb.Output({ format: new mb.Mp4OutputFormat({ fastStart: 'in-memory' }), target });
    const frames = new mb.CanvasSource(canvas, { codec: 'avc', bitrate: VIDEO_EXPORT.bitrate, keyFrameInterval: VIDEO_EXPORT.keyFrameSeconds });
    output.addVideoTrack(frames, { frameRate: fps });
    const audible = clips.some(layer => !layer.muted && layer.volume > 0 && files.get(layer.source.url).audio);
    const encodesSound = audible && (await aacReady(mb));
    const sound = encodesSound ? new mb.AudioBufferSource({ codec: 'aac', bitrate: VIDEO_EXPORT.audioBitrate }) : null;
    if (sound) output.addAudioTrack(sound);
    await output.start();

    let audio = audible ? 'dropped' : 'none';
    if (sound) {
      const mixed = await mixSound(mb, clips, files, duration);
      if (mixed) { await sound.add(mixed); audio = 'mixed'; }
      sound.close();
    }
    onProgress(0.05);

    // Every clip decodes exactly the frames it shows, in order, while the page is painted.
    const players = clips.map(layer => {
      const schedule = clipSchedule(layer, times, pageEnd);
      const size = decodeSize(layer);
      const sink = new mb.CanvasSink(files.get(layer.source.url).video, size ? { ...size, fit: 'fill' } : {});
      return { layer, schedule, next: 0, iterator: sink.canvasesAtTimestamps(schedule.timestamps), canvas: null };
    });
    for (let index = 0; index < times.length; index++) {
      for (const player of players) {
        if (player.schedule.frames[player.next] !== index) continue;
        player.next += 1;
        const { value } = await player.iterator.next();
        if (value?.canvas) player.canvas = value.canvas;
      }
      const shown = new Map(players.map(player => [player.layer.id, player.canvas]));
      drawPage(ctx, doc, page, { scale: 1, placeholders: false, time: Math.min(times[index], pageEnd), frames: layer => shown.get(layer.id) || null });
      await frames.add(times[index], 1 / fps);
      onProgress(0.05 + (0.93 * (index + 1)) / times.length);
    }
    await Promise.all(players.map(player => player.iterator.return?.()));
    frames.close();
    await output.finalize();
    onProgress(1);
    const blob = new Blob([target.buffer], { type: 'video/mp4' });
    return { blob, width, height, mime: 'video/mp4', extension: 'mp4', pageId: page.id, duration, audio };
  } catch (failure) {
    if (failure instanceof ArtExportError) throw failure;
    throw new ArtExportError(failure?.name === 'SecurityError' ? 'tainted' : 'video_render');
  } finally {
    dispose();
  }
}

